/**
 * Add Asset workflow.
 * Input methods: photo upload (single or multi-batch), live camera, barcode scan, manual entry.
 * AI extraction via Gemini Flash Lite.
 * Inline model creation stepper if model is missing.
 */

import { router } from '../router.js';
import { enqueue, getPending } from '../db.js';
import { syncDirect } from '../sync.js';
import { extractFromImage, extractFromMultipleImages, prepareForGemini } from '../gemini.js';
import { openCameraSession, closeCameraSession } from '../camera-session.js';
import { getModels, getModel, getStatusLabels, getAssetsWithPrefix, getAssetBySerial, getAssetByTag, getAsset, uploadAssetFile } from '../snipeit.js';
import { getSetting, MAC_COLUMN_WLAN, MAC_COLUMN_LAN, MAC_COLUMN_IP } from '../settings.js';
import { showToast } from '../toast.js';
import { hapticSuccess, hapticTap } from '../haptics.js';
import { openModelStepper } from './model-stepper.js';
import { startScanner, stopScanner } from './scanner.js';

let currentFieldsetId = null;
let currentImageFile = null;
let lastExtraction = null; // last Gemini extraction result, used to prefill model stepper
const multiImageFiles = new Map(); // card id → File/Blob for batch uploads
let deviceSessions = []; // array of { photos, data?, status: 'pending'|'extracting'|'ready'|'error', error? }

// ── Field-scan bottom sheet ────────────────────────────────────────────
function injectFieldScanSheet() {
  if (document.getElementById('field-scan-sheet')) return;
  const sheet = document.createElement('div');
  sheet.id = 'field-scan-sheet';
  sheet.className = 'field-scan-sheet hidden';
  sheet.innerHTML = `
    <div class="field-scan-backdrop"></div>
    <div class="field-scan-panel">
      <div class="field-scan-handle"></div>
      <p class="field-scan-title" id="field-scan-title">Scanning</p>
      <div class="camera-container" id="field-scan-reader"></div>
      <button type="button" class="btn btn-secondary btn-block btn-sm" id="field-scan-cancel">Cancel</button>
    </div>
  `;
  document.querySelector('.page').appendChild(sheet);
  document.getElementById('field-scan-cancel').addEventListener('click', closeFieldScanSheet);
  sheet.querySelector('.field-scan-backdrop').addEventListener('click', closeFieldScanSheet);
}

function closeFieldScanSheet() {
  stopScanner(); // stops whichever scanner is active (only one can run at a time)
  const sheet = document.getElementById('field-scan-sheet');
  if (sheet) sheet.classList.add('hidden');
}

function scanIntoField(fieldId, label) {
  closeFieldScanSheet(); // close any already-open sheet first
  injectFieldScanSheet();
  document.getElementById('field-scan-title').textContent = `Scanning: ${label}`;
  document.getElementById('field-scan-sheet').classList.remove('hidden');
  startScanner('field-scan-reader', (text) => {
    hapticSuccess();
    const input = document.getElementById(fieldId);
    input.value = text;
    // Dispatch both change and blur — blur triggers the serial duplicate-check handler
    input.dispatchEvent(new Event('change', { bubbles: true }));
    input.dispatchEvent(new Event('blur',   { bubbles: true }));
    closeFieldScanSheet();
    showToast('Scanned: ' + text);
  }, { narrow: true });
}

export function renderAddAsset() {
  const app = document.getElementById('app');

  app.innerHTML = `
    <div class="page">
      <!-- Screen-reader live region for extraction and search announcements -->
      <div id="aa-status" aria-live="polite" aria-atomic="true" style="position:absolute;width:1px;height:1px;overflow:hidden;clip:rect(0,0,0,0);white-space:nowrap;"></div>

      <div class="page-header">
        <button class="back-btn" id="back-btn" aria-label="Back">
          <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><polyline points="15 18 9 12 15 6"/></svg>
        </button>
        <h1>Add Asset</h1>
      </div>

      <!-- Input method buttons -->
      <div class="input-methods">
        <button class="btn btn-secondary" id="btn-camera">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M23 19a2 2 0 01-2 2H3a2 2 0 01-2-2V8a2 2 0 012-2h4l2-3h6l2 3h4a2 2 0 012 2z"/><circle cx="12" cy="13" r="4"/></svg>
          Camera
        </button>
        <button class="btn btn-secondary" id="btn-scan">
          <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><rect x="3" y="3" width="18" height="18" rx="2"/><line x1="7" y1="12" x2="17" y2="12"/></svg>
          Scan
        </button>
      </div>

      <!-- Barcode scanner area (hidden by default) -->
      <div id="scanner-area" class="hidden">
        <div class="camera-container" id="scanner-reader"></div>
        <button class="btn btn-secondary btn-block btn-sm" id="scanner-close">Close Scanner</button>
      </div>

      <!-- AI extraction result (hidden by default) -->
      <div id="extraction-result" class="hidden">
        <div class="extracted-data">
          <h3>AI Extracted Data</h3>
          <dl id="extracted-dl"></dl>
        </div>
      </div>

      <!-- Spinner for single-image extraction -->
      <div id="extraction-spinner" class="hidden" style="text-align:center; padding: 20px;">
        <div class="spinner"></div>
        <p style="margin-top:8px; font-size:0.85rem; color:var(--color-text-muted);">Analyzing label...</p>
      </div>

      <!-- Multi-device session area (shown after multi-device camera session) -->
      <div id="session-area" class="hidden">
        <div style="display:flex; justify-content:space-between; align-items:center; margin-bottom:12px;">
          <h3 style="font-size:0.8rem; font-weight:700; color:var(--color-text-muted); text-transform:uppercase; letter-spacing:0.055em; margin:0;">Devices</h3>
          <span id="session-device-count" style="font-size:0.8rem; color:var(--color-text-muted);"></span>
        </div>
        <div id="session-device-list"></div>
        <p style="font-size:0.78rem; color:var(--color-text-muted); margin: 10px 0 4px;">Status below applies to all queued assets.</p>
        <div style="display:flex; gap:8px; margin-top:12px;">
          <button type="button" class="btn btn-secondary" style="flex:1" id="btn-session-add">New Device</button>
          <button type="button" class="btn btn-secondary" style="flex:1" id="btn-session-cancel">Cancel</button>
          <button type="button" class="btn btn-primary" style="flex:1" id="btn-session-queue">Queue All</button>
        </div>
      </div>

      <!-- Asset form (single-asset entry) -->
      <form id="asset-form">
        <div class="form-group">
          <label for="asset-name">Asset Name / Title</label>
          <input type="text" id="asset-name" class="form-control" placeholder="e.g. HP ProBook 450 G9" required />
        </div>

        <div class="form-group">
          <label for="asset-tag">Asset Tag</label>
          <div style="display:flex;gap:6px;align-items:center;">
            <input type="text" id="asset-tag" class="form-control" placeholder="Auto-generated or enter manually" style="flex:1;" />
            <button type="button" class="btn btn-secondary btn-sm btn-scan-field" data-field="asset-tag" data-label="Asset Tag" aria-label="Scan asset tag"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9V6a1 1 0 011-1h2M3 15v3a1 1 0 001 1h2m11-16h2a1 1 0 011 1v3m0 6v3a1 1 0 01-1 1h-2M6 8v8M10 8v8M14 8v8M18 8v8"/></svg></button>
          </div>
        </div>

        <div class="form-group">
          <label for="asset-serial">Serial Number</label>
          <div style="display:flex;gap:6px;align-items:center;">
            <input type="text" id="asset-serial" class="form-control" placeholder="S/N" autocomplete="off" style="flex:1;" />
            <button type="button" class="btn btn-secondary btn-sm btn-scan-field" data-field="asset-serial" data-label="Serial Number" aria-label="Scan serial number"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9V6a1 1 0 011-1h2M3 15v3a1 1 0 001 1h2m11-16h2a1 1 0 011 1v3m0 6v3a1 1 0 01-1 1h-2M6 8v8M10 8v8M14 8v8M18 8v8"/></svg></button>
          </div>
        </div>
        <div id="serial-check" class="hidden"></div>

        <div class="form-group">
          <label for="asset-wlan-mac">WLAN MAC</label>
          <div style="display:flex;gap:6px;align-items:center;">
            <input type="text" id="asset-wlan-mac" class="form-control" placeholder="e.g. AA:BB:CC:DD:EE:FF" autocomplete="off" style="flex:1;" />
            <button type="button" class="btn btn-secondary btn-sm btn-scan-field" data-field="asset-wlan-mac" data-label="WLAN MAC" aria-label="Scan WLAN MAC"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9V6a1 1 0 011-1h2M3 15v3a1 1 0 001 1h2m11-16h2a1 1 0 011 1v3m0 6v3a1 1 0 01-1 1h-2M6 8v8M10 8v8M14 8v8M18 8v8"/></svg></button>
          </div>
        </div>

        <div class="form-group">
          <label for="asset-lan-mac">LAN MAC</label>
          <div style="display:flex;gap:6px;align-items:center;">
            <input type="text" id="asset-lan-mac" class="form-control" placeholder="e.g. AA:BB:CC:DD:EE:FF" autocomplete="off" style="flex:1;" />
            <button type="button" class="btn btn-secondary btn-sm btn-scan-field" data-field="asset-lan-mac" data-label="LAN MAC" aria-label="Scan LAN MAC"><svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M3 9V6a1 1 0 011-1h2M3 15v3a1 1 0 001 1h2m11-16h2a1 1 0 011 1v3m0 6v3a1 1 0 01-1 1h-2M6 8v8M10 8v8M14 8v8M18 8v8"/></svg></button>
          </div>
        </div>

        <div class="form-group" id="form-model-group">
          <label for="asset-model">Model</label>
          <div style="display:flex; gap:8px;">
            <select id="asset-model" class="form-control" style="flex:1;">
              <option value="">Select model...</option>
            </select>
            <button type="button" class="btn btn-secondary btn-sm" id="btn-new-model">+ New</button>
          </div>
        </div>

        <div class="form-group" id="form-status-group">
          <label for="asset-status">Status</label>
          <select id="asset-status" class="form-control">
            <option value="">Select status...</option>
          </select>
        </div>

        <div class="form-group">
          <label for="asset-notes">Notes</label>
          <textarea id="asset-notes" class="form-control" rows="2" placeholder="Optional notes..."></textarea>
        </div>

        <div style="display:flex; gap:8px; margin-top:20px;">
          <button type="button" class="btn btn-secondary" style="flex:1" id="btn-queue">Add to Queue</button>
          <button type="button" class="btn btn-primary" style="flex:1" id="btn-sync">Sync Now</button>
        </div>
      </form>
    </div>
  `;

  // ── Load dropdowns + next tag ──
  loadModels();
  loadStatuses();
  loadNextAssetTag().then(() => {
    const prefillTag = sessionStorage.getItem('prefill-tag');
    if (prefillTag) {
      sessionStorage.removeItem('prefill-tag');
      const tagInput = document.getElementById('asset-tag');
      if (tagInput) tagInput.value = prefillTag;
    }
  });

  // ── Navigation ──
  document.getElementById('back-btn').addEventListener('click', () => {
    router.navigate('/');
  });

  // ── Camera session (replaces old Upload + Camera buttons) ──
  document.getElementById('btn-camera').addEventListener('click', () => {
    hapticTap();
    openCameraForSession();
  });

  function openCameraForSession() {
    const deviceIndex = deviceSessions.length + 1;
    openCameraSession({
      onComplete: async (photos, reason) => {
        if (!photos.length) return;

        // Smart default: single photo + no prior sessions + done = instant single extraction
        if (photos.length === 1 && deviceSessions.length === 0 && reason === 'done') {
          await runExtraction(photos[0].blob, photos);
          return;
        }

        // Add this device to the session
        const session = { photos, data: null, status: 'pending', error: null };
        deviceSessions.push(session);

        if (reason === 'next') {
          // Reopen camera for next device
          openCameraForSession();
        } else {
          // Done — show all device cards and extract
          showSessionArea();
        }
      },
      onCancel: () => {
        // If we already have sessions, show session area; otherwise do nothing
        if (deviceSessions.length > 0) showSessionArea();
      },
      deviceIndex,
    });
  }

  // ── Barcode scanner ──
  document.getElementById('btn-scan').addEventListener('click', async () => {
    hapticTap();
    const scannerArea = document.getElementById('scanner-area');
    scannerArea.classList.remove('hidden');

    startScanner('scanner-reader', async (text) => {
      hapticSuccess();
      stopScanner();
      scannerArea.classList.add('hidden');
      document.getElementById('asset-tag').value = text;
      showToast('Scanned: ' + text);
      try {
        const { tagAsset } = await checkDuplicates('', text);
        if (tagAsset) {
          showDuplicateModal(
            [{ field: 'tag', value: text, asset: tagAsset }],
            resetAssetForm
          );
        }
      } catch { /* offline — silent */ }
    }, { narrow: true });
  });

  document.getElementById('scanner-close').addEventListener('click', () => {
    stopScanner();
    document.getElementById('scanner-area').classList.add('hidden');
  });

  // ── Model change → fetch fieldset ──
  document.getElementById('asset-model').addEventListener('change', async (e) => {
    const id = e.target.value;
    currentFieldsetId = null;
    if (!id) return;
    try {
      const m = await getModel(parseInt(id, 10));
      currentFieldsetId = m.fieldset?.id || null;
    } catch { /* non-fatal */ }
  });

  // ── New model stepper ──
  document.getElementById('btn-new-model').addEventListener('click', () => {
    const manufacturerName = lastExtraction?.title?.split(' ')[0] ?? '';
    openModelStepper(({ id, name, fieldset_id }) => {
      const select = document.getElementById('asset-model');
      const opt = document.createElement('option');
      opt.value = id;
      opt.textContent = name;
      opt.selected = true;
      select.appendChild(opt);
      currentFieldsetId = fieldset_id || null;
      showToast('Model created');
    }, {
      manufacturerName,
      modelName: lastExtraction?.title ?? '',
      modelNumber: lastExtraction?.details?.['P/N'] ?? '',
    });
  });

  // Per-field scan buttons (delegated) — stored for cleanup
  const onFieldScanClick = e => {
    const btn = e.target.closest('.btn-scan-field');
    if (!btn) return;
    hapticTap();
    scanIntoField(btn.dataset.field, btn.dataset.label);
  };
  app.addEventListener('click', onFieldScanClick);

  // ── Serial duplicate check (manual entry) ──
  document.getElementById('asset-serial').addEventListener('blur', async (e) => {
    const serial = e.target.value.trim();
    if (!serial) return;
    try {
      const { serialAsset } = await checkDuplicates(serial, '');
      if (serialAsset) {
        showDuplicateModal(
          [{ field: 'serial', value: serial, asset: serialAsset }],
          resetAssetForm
        );
      }
    } catch { /* offline — silent */ }
  });

  // ── Session area controls ──
  document.getElementById('btn-session-cancel').addEventListener('click', () => {
    deviceSessions = [];
    document.getElementById('session-area').classList.add('hidden');
    document.getElementById('asset-form').classList.remove('hidden');
    document.getElementById('extraction-result').classList.add('hidden');
    loadNextAssetTag();
  });

  document.getElementById('btn-session-add').addEventListener('click', () => {
    hapticTap();
    openCameraForSession();
  });

  document.getElementById('btn-session-queue').addEventListener('click', queueAllSessions);

  // ── Single-asset form submit ──
  document.getElementById('btn-queue').addEventListener('click', () => submitAsset(false));
  document.getElementById('btn-sync').addEventListener('click', () => submitAsset(true));

  // Cleanup
  return () => {
    closeCameraSession();
    stopScanner();
    deviceSessions = [];
    multiImageFiles.clear();
    document.getElementById('field-scan-sheet')?.remove();
    app.removeEventListener('click', onFieldScanClick);
  };
}

// ── Asset tag helpers ──────────────────────────────────────────────────────

/**
 * Returns the highest numeric suffix currently in use across both Snipe-IT
 * and the local pending queue for the given prefix.
 */
async function computeBaseTagNumber(prefix) {
  let max = 0;

  // Check Snipe-IT database
  try {
    const res = await getAssetsWithPrefix(prefix);
    for (const asset of res.rows || []) {
      const tag = asset.asset_tag || '';
      if (tag.startsWith(prefix)) {
        const n = parseInt(tag.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    }
  } catch { /* offline — fall through */ }

  // Check pending queue for already-assigned tags
  try {
    const pending = await getPending();
    for (const item of pending) {
      if (item.type !== 'create_asset') continue;
      const tag = item.payload?.asset_tag || '';
      if (tag.startsWith(prefix)) {
        const n = parseInt(tag.slice(prefix.length), 10);
        if (!isNaN(n) && n > max) max = n;
      }
    }
  } catch { /* ignore */ }

  return max;
}

async function loadNextAssetTag() {
  const prefix = getSetting('assetTagPrefix');
  const el = document.getElementById('asset-tag');
  if (!el) return;

  if (!prefix) {
    el.placeholder = 'Enter asset tag';
    return;
  }

  el.placeholder = `Loading next ${prefix} tag...`;

  try {
    const base = await computeBaseTagNumber(prefix);
    el.value = `${prefix}${String(base + 1).padStart(4, '0')}`;
    el.placeholder = '';
  } catch {
    el.placeholder = `${prefix}XXXX (enter manually)`;
  }
}

// ── Model / status loaders ─────────────────────────────────────────────────

async function loadModels() {
  const select = document.getElementById('asset-model');
  if (!select) return;
  const loadingOpt = document.createElement('option');
  loadingOpt.disabled = true;
  loadingOpt.textContent = 'Loading...';
  select.insertBefore(loadingOpt, select.firstChild);
  try {
    const res = await getModels();
    loadingOpt.remove();
    (res.rows || []).forEach((m) => {
      const opt = document.createElement('option');
      opt.value = m.id;
      opt.textContent = m.name;
      select.appendChild(opt);
    });
  } catch {
    loadingOpt.textContent = 'Failed to load';
  }
}

async function loadStatuses() {
  const select = document.getElementById('asset-status');
  if (!select) return;
  const loadingOpt = document.createElement('option');
  loadingOpt.disabled = true;
  loadingOpt.textContent = 'Loading...';
  select.insertBefore(loadingOpt, select.firstChild);
  try {
    const res = await getStatusLabels();
    loadingOpt.remove();
    (res.rows || []).forEach((s) => {
      const opt = document.createElement('option');
      opt.value = s.id;
      opt.textContent = s.name;
      select.appendChild(opt);
    });
    // Default to "Newly Arrived" status
    const opts = Array.from(select.options);
    const match = opts.find((o) =>
      o.textContent.toLowerCase().replace(/\s+/g, ' ').includes('new') &&
      o.textContent.toLowerCase().includes('arriv')
    );
    if (match) match.selected = true;
  } catch {
    loadingOpt.textContent = 'Failed to load';
  }
}

// ── Single-image extraction ────────────────────────────────────────────────

async function runExtraction(file, photos = null) {
  const spinner = document.getElementById('extraction-spinner');
  const result = document.getElementById('extraction-result');
  spinner.classList.remove('hidden');
  result.classList.add('hidden');

  // Save the image file for upload after asset creation
  currentImageFile = file;

  try {
    const { base64, mimeType } = await prepareForGemini(file);
    let data = await extractFromImage(base64, mimeType);
    // Reconcile barcodes if photos with barcode data were passed
    if (photos) data = reconcileBarcodes(data, photos);
    populateFormFromExtraction(data);
    hapticSuccess();
    showToast('Data extracted');
    document.getElementById('aa-status').textContent = 'Data extracted. Form fields updated.';
  } catch (err) {
    showToast('Extraction failed: ' + err.message);
    document.getElementById('aa-status').textContent = 'Extraction failed: ' + err.message;
  } finally {
    spinner.classList.add('hidden');
  }
}

/**
 * Multi-photo extraction — sends all photos of one device to Gemini
 * in a single call for cross-referencing.
 */
async function runMultiPhotoExtraction(photos) {
  const spinner = document.getElementById('extraction-spinner');
  const result = document.getElementById('extraction-result');
  spinner.classList.remove('hidden');
  result.classList.add('hidden');

  // Save first photo for asset file upload
  currentImageFile = photos[0].blob;

  try {
    const prepared = await Promise.all(
      photos.map((p) => prepareForGemini(p.blob))
    );
    const rawData = await extractFromMultipleImages(prepared);
    const data = reconcileBarcodes(rawData, photos);
    populateFormFromExtraction(data);
    hapticSuccess();
    const barcodeCount = data._barcodes?.length || 0;
    const msg = barcodeCount
      ? `Extracted from ${photos.length} photos (${barcodeCount} barcode${barcodeCount > 1 ? 's' : ''} found)`
      : `Extracted from ${photos.length} photos`;
    showToast(msg);
    document.getElementById('aa-status').textContent = 'Data extracted from multiple photos. Form fields updated.';
  } catch (err) {
    showToast('Extraction failed: ' + err.message);
    document.getElementById('aa-status').textContent = 'Extraction failed: ' + err.message;
  } finally {
    spinner.classList.add('hidden');
  }
}

/**
 * Cross-reference barcode values with Gemini extraction.
 * - If barcode matches extracted serial → mark confirmed
 * - If serial is empty and a barcode looks like a serial → fill it
 * Returns enriched data with confidence indicators.
 */
function reconcileBarcodes(data, photos) {
  const allBarcodes = photos.flatMap((p) => p.barcodes || []);
  if (allBarcodes.length === 0) return data;

  const confidence = {};
  const serial = data.details?.['S/N'] || '';
  const partNum = data.details?.['P/N'] || '';

  // Check if any barcode matches the serial
  if (serial && allBarcodes.some((b) => b === serial)) {
    confidence['S/N'] = 'barcode';
  }

  // Check if any barcode matches the part number
  if (partNum && allBarcodes.some((b) => b === partNum)) {
    confidence['P/N'] = 'barcode';
  }

  // If serial is empty, try to fill from barcode
  if (!serial && allBarcodes.length > 0) {
    // Pick the first barcode that looks like a serial (alphanumeric, 5+ chars)
    const candidate = allBarcodes.find((b) => /^[A-Za-z0-9]{5,}$/.test(b));
    if (candidate) {
      data.details = data.details || {};
      data.details['S/N'] = candidate;
      confidence['S/N'] = 'barcode';
    }
  }

  data._confidence = confidence;
  data._barcodes = allBarcodes;
  return data;
}

/**
 * Populate the single-asset form from extracted data.
 * Shared by runExtraction() and runMultiPhotoExtraction().
 */
function populateFormFromExtraction(data) {
  lastExtraction = data;

  if (data.title) {
    document.getElementById('asset-name').value = data.title;
    tryMatchModel(data.title);
  }
  if (data.details?.['S/N']) {
    const serial = data.details['S/N'];
    document.getElementById('asset-serial').value = serial;
    checkDuplicates(serial, '').then(({ serialAsset }) => {
      if (serialAsset) {
        showDuplicateModal(
          [{ field: 'serial', value: serial, asset: serialAsset }],
          resetAssetForm
        );
      }
    }).catch(() => { /* offline — silent */ });
  }
  if (data.details?.['WLAN MAC']) {
    document.getElementById('asset-wlan-mac').value = data.details['WLAN MAC'];
  }
  if (data.details?.['LAN MAC']) {
    document.getElementById('asset-lan-mac').value = data.details['LAN MAC'];
  }

  const dl = document.getElementById('extracted-dl');
  dl.innerHTML = '';
  if (data.title) dl.innerHTML += `<dt>Model</dt><dd>${escapeHtml(data.title)}</dd>`;
  const confidence = data._confidence || {};
  for (const [key, val] of Object.entries(data.details || {})) {
    if (!val) continue;
    const conf = confidence[key];
    const badge = conf === 'barcode'
      ? ' <span class="confidence-badge confidence-barcode" title="Verified by barcode">barcode</span>'
      : '';
    dl.innerHTML += `<dt>${escapeHtml(key)}</dt><dd>${escapeHtml(val)}${badge}</dd>`;
  }
  document.getElementById('extraction-result').classList.remove('hidden');
}

// ── Multi-device session area ──────────────────────────────────────────────

async function showSessionArea() {
  document.getElementById('asset-form').classList.add('hidden');
  document.getElementById('extraction-result').classList.add('hidden');
  document.getElementById('session-area').classList.remove('hidden');

  const list = document.getElementById('session-device-list');
  list.innerHTML = '';
  multiImageFiles.clear();
  document.getElementById('session-device-count').textContent =
    `${deviceSessions.length} device${deviceSessions.length !== 1 ? 's' : ''}`;

  // Create a card per device
  for (let i = 0; i < deviceSessions.length; i++) {
    const card = createSessionCard(i);
    list.appendChild(card);
    populateSessionCardModel(card);
    // Associate first photo for upload after asset creation
    multiImageFiles.set(`sdc-${i}`, deviceSessions[i].photos[0]?.blob || null);
  }

  // Extract each device sequentially
  for (let i = 0; i < deviceSessions.length; i++) {
    const session = deviceSessions[i];
    const card = document.getElementById(`sdc-${i}`);
    if (!card) continue;

    // Skip re-extraction for already-extracted devices
    if (session.status === 'ready' && session.data) {
      fillSessionCard(card, session.data);
      continue;
    }

    session.status = 'extracting';
    try {
      let data;
      if (session.photos.length === 1) {
        const { base64, mimeType } = await prepareForGemini(session.photos[0].blob);
        data = await extractFromImage(base64, mimeType);
      } else {
        const prepared = await Promise.all(session.photos.map((p) => prepareForGemini(p.blob)));
        data = await extractFromMultipleImages(prepared);
      }
      data = reconcileBarcodes(data, session.photos);
      session.data = data;
      session.status = 'ready';
      fillSessionCard(card, data);
      navigator.vibrate?.(8);
    } catch (err) {
      session.status = 'error';
      session.error = err.message;
      showSessionCardError(card, err.message);
      navigator.vibrate?.([30, 50, 30, 50, 30]);
    }
  }
}

function createSessionCard(index) {
  const session = deviceSessions[index];
  const photoCount = session.photos.length;
  const card = document.createElement('div');
  card.className = 'multi-asset-card';
  card.id = `sdc-${index}`;
  card.dataset.fieldsetId = '';
  card.innerHTML = `
    <div class="mac-header">
      <span style="font-size:0.75rem; color:var(--color-text-muted); font-weight:600;">Device ${index + 1} (${photoCount} photo${photoCount !== 1 ? 's' : ''})</span>
      <button type="button" class="mac-remove" aria-label="Remove device ${index + 1}">
        <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5"><line x1="18" y1="6" x2="6" y2="18"/><line x1="6" y1="6" x2="18" y2="18"/></svg>
      </button>
    </div>
    <div class="mac-processing">
      <span class="spinner"></span>
      <span style="font-size:0.85rem; color:var(--color-text-muted);">Analyzing ${photoCount} photo${photoCount !== 1 ? 's' : ''}...</span>
    </div>
    <div class="mac-fields hidden">
      <input type="text" class="form-control mac-name" placeholder="Asset name / title" />
      <select class="form-control mac-model" style="margin-top:8px;"></select>
      <input type="text" class="form-control mac-serial" placeholder="S/N" style="margin-top:8px;" />
      <div style="display:grid; grid-template-columns:1fr 1fr; gap:8px; margin-top:8px;">
        <input type="text" class="form-control mac-wlan" placeholder="WLAN MAC" />
        <input type="text" class="form-control mac-lan" placeholder="LAN MAC" />
      </div>
      <input type="text" class="form-control mac-tag-input" placeholder="Asset tag (auto)" style="margin-top:8px;" />
    </div>
    <div class="mac-error hidden"></div>
  `;

  card.querySelector('.mac-remove').addEventListener('click', () => {
    deviceSessions.splice(index, 1);
    card.remove();
    document.getElementById('session-device-count').textContent =
      `${deviceSessions.length} device${deviceSessions.length !== 1 ? 's' : ''}`;
  });

  return card;
}

function populateSessionCardModel(card) {
  const source = document.getElementById('asset-model');
  const dest = card.querySelector('.mac-model');
  if (!source || !dest) return;
  dest.innerHTML = '';
  Array.from(source.options).forEach((opt) => {
    dest.appendChild(opt.cloneNode(true));
  });
  dest.addEventListener('change', async (e) => {
    card.dataset.fieldsetId = '';
    if (!e.target.value) return;
    try {
      const m = await getModel(parseInt(e.target.value, 10));
      card.dataset.fieldsetId = m.fieldset?.id || '';
    } catch { /* non-fatal */ }
  });
}

function fillSessionCard(card, data) {
  card.querySelector('.mac-processing').classList.add('hidden');
  const fields = card.querySelector('.mac-fields');
  fields.classList.remove('hidden');

  if (data.title) {
    card.querySelector('.mac-name').value = data.title;
    const select = card.querySelector('.mac-model');
    const t = data.title.toLowerCase().trim();
    const match = Array.from(select.options).find((o) => {
      if (!o.value) return false;
      const n = o.textContent.toLowerCase().trim();
      return n === t || t.includes(n) || n.includes(t);
    });
    if (match) match.selected = true;
  }
  if (data.details?.['S/N']) card.querySelector('.mac-serial').value = data.details['S/N'];
  if (data.details?.['WLAN MAC']) card.querySelector('.mac-wlan').value = data.details['WLAN MAC'];
  if (data.details?.['LAN MAC']) card.querySelector('.mac-lan').value = data.details['LAN MAC'];

  // Show confidence badges if available
  const confidence = data._confidence || {};
  for (const [key, conf] of Object.entries(confidence)) {
    if (conf !== 'barcode') continue;
    const fieldMap = { 'S/N': '.mac-serial', 'P/N': null, 'WLAN MAC': '.mac-wlan', 'LAN MAC': '.mac-lan' };
    const selector = fieldMap[key];
    if (!selector) continue;
    const input = card.querySelector(selector);
    if (input) input.style.borderColor = 'var(--color-success)';
  }
}

function showSessionCardError(card, message) {
  card.querySelector('.mac-processing').classList.add('hidden');
  const err = card.querySelector('.mac-error');
  err.textContent = 'Extraction failed: ' + message;
  err.classList.remove('hidden');
  // Show empty fields so user can fill manually
  card.querySelector('.mac-fields').classList.remove('hidden');
}

async function queueAllSessions() {
  const cards = Array.from(document.querySelectorAll('[id^="sdc-"]'));
  if (!cards.length) {
    showToast('No devices to queue');
    return;
  }

  const btn = document.getElementById('btn-session-queue');
  btn.disabled = true;
  btn.textContent = 'Checking...';

  // Batch duplicate gate
  try {
    const checkResults = await Promise.all(
      cards.map((card) => {
        const serial = card.querySelector('.mac-serial')?.value.trim() || '';
        const tag = card.querySelector('.mac-tag-input')?.value.trim() || '';
        return checkDuplicates(serial, tag).then((r) => ({ card, serial, tag, ...r }));
      })
    );
    const batchDups = checkResults.filter((r) => r.serialAsset || r.tagAsset);
    if (batchDups.length) {
      const dups = batchDups.flatMap((r) => {
        const items = [];
        if (r.serialAsset) items.push({ field: 'serial', value: r.serial, asset: r.serialAsset, card: r.card });
        if (r.tagAsset) items.push({ field: 'tag', value: r.tag, asset: r.tagAsset, card: r.card });
        return items;
      });
      showDuplicateModal(dups, () => {
        const dupCards = new Set(batchDups.map((r) => r.card));
        dupCards.forEach((c) => c.remove());
        btn.disabled = false;
        btn.textContent = 'Queue All';
        queueAllSessions();
      });
      btn.disabled = false;
      btn.textContent = 'Queue All';
      return;
    }
  } catch { /* network error — allow queuing */ }

  btn.textContent = 'Queuing...';

  // Assign tags
  const cleanCards = Array.from(document.querySelectorAll('[id^="sdc-"]'));
  const prefix = getSetting('assetTagPrefix');
  if (prefix) {
    try {
      const base = await computeBaseTagNumber(prefix);
      let nextNum = base + 1;
      for (const card of cleanCards) {
        const tagInput = card.querySelector('.mac-tag-input');
        if (!tagInput.value.trim()) {
          tagInput.value = `${prefix}${String(nextNum).padStart(4, '0')}`;
          nextNum++;
        }
      }
    } catch { /* offline — tags fall back */ }
  }

  const statusId = document.getElementById('asset-status').value;
  let count = 0;
  for (const card of cleanCards) {
    const name = card.querySelector('.mac-name')?.value.trim();
    if (!name) continue;

    const tag = card.querySelector('.mac-tag-input')?.value.trim();
    const serial = card.querySelector('.mac-serial')?.value.trim();
    const wlanMac = card.querySelector('.mac-wlan')?.value.trim();
    const lanMac = card.querySelector('.mac-lan')?.value.trim();
    const modelSelect = card.querySelector('.mac-model');
    const modelId = modelSelect?.value;
    const modelName = modelSelect?.options[modelSelect.selectedIndex]?.text || '';

    const payload = {
      name,
      asset_tag: tag || `ASSET-${Date.now().toString().slice(-6)}-${count}`,
      serial: serial || '',
    };

    if (modelId) payload.model_id = parseInt(modelId, 10);
    if (modelId && modelName) payload.model_name = modelName;
    if (statusId) payload.status_id = parseInt(statusId, 10);
    const fieldsetId = card.dataset.fieldsetId;
    if (fieldsetId) payload.fieldset_id = parseInt(fieldsetId, 10);
    if (wlanMac) payload[MAC_COLUMN_WLAN] = wlanMac;
    if (lanMac) payload[MAC_COLUMN_LAN] = lanMac;

    const imageFile = multiImageFiles.get(card.id) || null;
    await enqueue('create_asset', payload, imageFile);
    count++;
  }

  multiImageFiles.clear();
  deviceSessions = [];
  hapticSuccess();
  showToast(`${count} asset${count !== 1 ? 's' : ''} added to queue`);
  window.dispatchEvent(new CustomEvent('queue-updated'));
  router.navigate('/');
}

// (Old batch functions and camera code removed — replaced by session flow above)

// ── Single-asset submit ────────────────────────────────────────────────────

async function submitAsset(direct) {
  const name = document.getElementById('asset-name').value.trim();
  const tag = document.getElementById('asset-tag').value.trim();
  const serial = document.getElementById('asset-serial').value.trim();
  const wlanMac = document.getElementById('asset-wlan-mac').value.trim();
  const lanMac = document.getElementById('asset-lan-mac').value.trim();
  const modelId = document.getElementById('asset-model').value;
  const statusId = document.getElementById('asset-status').value;
  const notes = document.getElementById('asset-notes').value.trim();

  if (!name) {
    showToast('Asset name is required');
    const nameEl = document.getElementById('asset-name');
    nameEl.setAttribute('aria-invalid', 'true');
    nameEl.focus();
    return;
  }

  document.getElementById('asset-name').removeAttribute('aria-invalid');

  // ── Duplicate gate ──
  try {
    const dupResult = await checkDuplicates(serial, tag);
    const dups = [];
    if (dupResult.serialAsset) dups.push({ field: 'serial', value: serial, asset: dupResult.serialAsset });
    if (dupResult.tagAsset)    dups.push({ field: 'tag',    value: tag,    asset: dupResult.tagAsset });
    if (dups.length) {
      showDuplicateModal(dups, resetAssetForm);
      return;
    }
  } catch { /* network error — allow submission to proceed */ }

  const payload = {
    name,
    asset_tag: tag || `ASSET-${Date.now().toString().slice(-6)}`,
    serial,
    notes,
  };

  if (modelId) payload.model_id = parseInt(modelId, 10);
  const modelEl = document.getElementById('asset-model');
  const modelName = modelEl.options[modelEl.selectedIndex]?.text || '';
  if (modelId && modelName) payload.model_name = modelName;
  if (statusId) payload.status_id = parseInt(statusId, 10);
  if (currentFieldsetId) payload.fieldset_id = currentFieldsetId;

  if (wlanMac) payload[MAC_COLUMN_WLAN] = wlanMac;
  if (lanMac) payload[MAC_COLUMN_LAN] = lanMac;

  const syncBtn = document.getElementById('btn-sync');
  const queueBtn = document.getElementById('btn-queue');

  const imageFile = currentImageFile;

  if (direct) {
    syncBtn.disabled = true;
    syncBtn.textContent = 'Syncing...';
    try {
      const result = await syncDirect('create_asset', payload);
      // Upload creation image if we have one
      if (imageFile && result?.payload?.id) {
        try {
          await uploadAssetFile(result.payload.id, imageFile, 'Creation Image');
        } catch { /* non-fatal */ }
      }
      hapticSuccess();
      showToast('Asset created');
      currentImageFile = null;
      router.navigate('/');
    } catch (err) {
      showToast('Sync failed: ' + err.message);
      syncBtn.disabled = false;
      syncBtn.textContent = 'Sync Now';
    }
  } else {
    queueBtn.disabled = true;
    await enqueue('create_asset', payload, imageFile);
    currentImageFile = null;
    hapticSuccess();
    showToast('Added to queue');
    window.dispatchEvent(new CustomEvent('queue-updated'));
    router.navigate('/');
  }
}

// ── Duplicate gate helpers ─────────────────────────────────────────────────

function resetAssetForm() {
  ['asset-name', 'asset-serial', 'asset-wlan-mac', 'asset-lan-mac', 'asset-notes'].forEach((id) => {
    const el = document.getElementById(id);
    if (el) el.value = '';
  });

  const modelSel = document.getElementById('asset-model');
  if (modelSel) modelSel.selectedIndex = 0;
  currentFieldsetId = null;
  currentImageFile = null;

  const statusSel = document.getElementById('asset-status');
  if (statusSel) {
    const match = Array.from(statusSel.options).find((o) =>
      o.textContent.toLowerCase().replace(/\s+/g, ' ').includes('new') &&
      o.textContent.toLowerCase().includes('arriv')
    );
    if (match) match.selected = true;
    else statusSel.selectedIndex = 0;
  }

  loadNextAssetTag();

  const sc = document.getElementById('serial-check');
  if (sc) { sc.className = 'hidden'; sc.innerHTML = ''; }

  const er = document.getElementById('extraction-result');
  if (er) er.classList.add('hidden');
}

async function checkDuplicates(serial, tag) {
  const [serialRes, tagRes, pending] = await Promise.all([
    serial ? getAssetBySerial(serial) : Promise.resolve(null),
    tag    ? getAssetByTag(tag)       : Promise.resolve(null),
    getPending(),
  ]);
  const resolveFirst = (r) => {
    if (!r) return null;
    if (Array.isArray(r.rows) && r.rows.length) return r.rows[0];
    if (r.id) return r;
    return null;
  };

  let serialAsset = resolveFirst(serialRes);
  let tagAsset    = resolveFirst(tagRes);

  // Check pending queue for create_asset items — Snipe-IT result takes priority
  for (const item of pending) {
    if (item.type !== 'create_asset') continue;
    const p = item.payload;
    if (!serialAsset && serial && p.serial && p.serial === serial) {
      serialAsset = { ...p, _queueSource: true };
    }
    if (!tagAsset && tag && p.asset_tag && p.asset_tag === tag) {
      tagAsset = { ...p, _queueSource: true };
    }
    if (serialAsset && tagAsset) break;
  }

  return { serialAsset, tagAsset };
}

function showDuplicateModal(duplicates, onClear) {
  const overlay = document.createElement('div');
  overlay.style.cssText = `
    position:fixed; inset:0; z-index:950;
    background:rgba(0,0,0,0.5);
    display:flex; align-items:flex-end; justify-content:center;
  `;

  const sheet = document.createElement('div');
  sheet.style.cssText = `
    background:var(--color-surface);
    border-radius:var(--radius-lg) var(--radius-lg) 0 0;
    width:100%; max-width:480px;
    max-height:90dvh; overflow-y:auto;
    padding:20px 16px calc(16px + var(--safe-bottom));
  `;
  overlay.appendChild(sheet);
  document.body.appendChild(overlay);

  function close() { overlay.remove(); }

  function renderSummary() {
    const title = duplicates.length === 1 ? 'Duplicate Found' : 'Duplicates Found';
    const dupItems = duplicates.map((d) => {
      const a = d.asset;
      const isQueue = !!a._queueSource;
      const suffix = isQueue ? 'already in queue' : 'already exists';
      const fieldLabel = d.field === 'serial'
        ? `Serial &ldquo;${escapeHtml(d.value)}&rdquo; ${suffix}`
        : `Asset tag &ldquo;${escapeHtml(d.value)}&rdquo; ${suffix}`;
      const modelName = isQueue ? (a.model_name || '—') : (a.model?.name || '—');
      const statusRow = isQueue ? '' :
        `<dt style="color:var(--color-text-muted);">Status</dt><dd style="margin:0;">${escapeHtml(a.status_label?.name || '—')}</dd>`;
      const assignedTo = isQueue ? null : (a.assigned_to?.name || null);
      const editBtn = isQueue ? '' :
        `<button class="btn btn-secondary btn-sm" style="width:100%;" data-asset-id="${a.id}" data-asset-name="${escapeAttr(a.name || '')}">Edit Existing Asset →</button>`;
      return `
        <div style="border:1px solid var(--color-border); border-radius:var(--radius-md); padding:12px; margin-bottom:12px;">
          <div style="font-size:0.8rem; font-weight:600; color:#f59e0b; margin-bottom:8px; display:flex; align-items:center; gap:6px;">
            <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" aria-hidden="true"><path d="M10.29 3.86L1.82 18a2 2 0 001.71 3h16.94a2 2 0 001.71-3L13.71 3.86a2 2 0 00-3.42 0z"/><line x1="12" y1="9" x2="12" y2="13"/><line x1="12" y1="17" x2="12.01" y2="17"/></svg>
            ${fieldLabel}
          </div>
          <dl style="display:grid; grid-template-columns:auto 1fr; gap:2px 10px; font-size:0.82rem; margin:0 0 10px;">
            <dt style="color:var(--color-text-muted);">Tag</dt><dd style="margin:0;">${escapeHtml(a.asset_tag || '—')}</dd>
            <dt style="color:var(--color-text-muted);">Name</dt><dd style="margin:0;">${escapeHtml(a.name || '—')}</dd>
            <dt style="color:var(--color-text-muted);">Model</dt><dd style="margin:0;">${escapeHtml(modelName)}</dd>
            ${statusRow}
            ${assignedTo ? `<dt style="color:var(--color-text-muted);">Assigned</dt><dd style="margin:0;">${escapeHtml(assignedTo)}</dd>` : ''}
          </dl>
          ${editBtn}
        </div>
      `;
    }).join('');

    sheet.innerHTML = `
      <h2 style="margin:0 0 16px;">${title}</h2>
      ${dupItems}
      <button class="btn btn-secondary btn-block" id="dup-discard">Discard &amp; Go Back</button>
    `;

    sheet.querySelectorAll('[data-asset-id]').forEach((btn) => {
      btn.addEventListener('click', () => renderEditForm(parseInt(btn.dataset.assetId, 10), btn.dataset.assetName));
    });

    sheet.querySelector('#dup-discard').addEventListener('click', () => {
      close();
      onClear();
    });
  }

  async function renderEditForm(assetId, assetName) {
    sheet.innerHTML = `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:16px;">
        <button class="btn btn-secondary btn-sm" id="dup-back">← Back</button>
        <h2 style="margin:0; font-size:1rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">Edit: ${escapeHtml(assetName)}</h2>
      </div>
      <div id="dup-edit-loading" style="text-align:center; padding:20px;">
        <div class="spinner"></div>
        <p style="margin-top:8px; font-size:0.85rem; color:var(--color-text-muted);">Loading asset…</p>
      </div>
    `;
    sheet.querySelector('#dup-back').addEventListener('click', renderSummary);

    let full;
    try {
      full = await getAsset(assetId);
    } catch (err) {
      sheet.querySelector('#dup-edit-loading').innerHTML =
        `<p style="color:var(--color-danger,#ef4444);">Failed to load asset: ${escapeHtml(err.message)}</p>`;
      return;
    }

    // Clone model and status options from already-loaded dropdowns on the background form
    const cloneOptions = (sourceId) => {
      const source = document.getElementById(sourceId);
      if (!source) return '<option value="">—</option>';
      return Array.from(source.options).map((o) =>
        `<option value="${escapeAttr(o.value)}">${escapeHtml(o.textContent)}</option>`
      ).join('');
    };

    sheet.innerHTML = `
      <div style="display:flex; align-items:center; gap:10px; margin-bottom:16px;">
        <button class="btn btn-secondary btn-sm" id="dup-back">← Back</button>
        <h2 style="margin:0; font-size:1rem; overflow:hidden; text-overflow:ellipsis; white-space:nowrap;">Edit: ${escapeHtml(full.name || assetName)}</h2>
      </div>
      <div class="form-group">
        <label for="dup-edit-name">Asset Name</label>
        <input type="text" id="dup-edit-name" class="form-control" value="${escapeAttr(full.name || '')}" />
      </div>
      <div class="form-group">
        <label for="dup-edit-serial">Serial Number</label>
        <input type="text" id="dup-edit-serial" class="form-control" value="${escapeAttr(full.serial || '')}" />
      </div>
      <div class="form-group">
        <label for="dup-edit-model">Model</label>
        <select id="dup-edit-model" class="form-control">${cloneOptions('asset-model')}</select>
      </div>
      <div class="form-group">
        <label for="dup-edit-status">Status</label>
        <select id="dup-edit-status" class="form-control">${cloneOptions('asset-status')}</select>
      </div>
      <div class="form-group">
        <label for="dup-edit-wlan">WLAN MAC</label>
        <input type="text" id="dup-edit-wlan" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_WLAN]?.value || '')}" />
      </div>
      <div class="form-group">
        <label for="dup-edit-lan">LAN MAC</label>
        <input type="text" id="dup-edit-lan" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_LAN]?.value || '')}" />
      </div>
      <div class="form-group">
        <label for="dup-edit-ip">IP Address</label>
        <input type="text" id="dup-edit-ip" class="form-control" value="${escapeAttr(full.custom_fields?.[MAC_COLUMN_IP]?.value || '')}" />
      </div>
      <div class="form-group">
        <label for="dup-edit-notes">Notes</label>
        <textarea id="dup-edit-notes" class="form-control" rows="2">${escapeHtml(full.notes || '')}</textarea>
      </div>
      <div style="display:flex; gap:8px; margin-top:20px;">
        <button class="btn btn-secondary" style="flex:1" id="dup-edit-queue">Add to Queue</button>
        <button class="btn btn-primary" style="flex:1" id="dup-edit-sync">Sync Now</button>
      </div>
    `;

    // Pre-select model and status
    const modelSel = sheet.querySelector('#dup-edit-model');
    const statusSel = sheet.querySelector('#dup-edit-status');
    if (full.model?.id) {
      const opt = Array.from(modelSel.options).find((o) => o.value == full.model.id);
      if (opt) opt.selected = true;
    }
    if (full.status_label?.id) {
      const opt = Array.from(statusSel.options).find((o) => o.value == full.status_label.id);
      if (opt) opt.selected = true;
    }

    sheet.querySelector('#dup-back').addEventListener('click', renderSummary);

    function buildEditPayload() {
      return {
        asset_id: assetId,
        name: sheet.querySelector('#dup-edit-name').value.trim(),
        serial: sheet.querySelector('#dup-edit-serial').value.trim(),
        notes: sheet.querySelector('#dup-edit-notes').value.trim(),
        ...(modelSel.value ? { model_id: parseInt(modelSel.value, 10) } : {}),
        ...(statusSel.value ? { status_id: parseInt(statusSel.value, 10) } : {}),
        ...(sheet.querySelector('#dup-edit-wlan').value.trim() ? { [MAC_COLUMN_WLAN]: sheet.querySelector('#dup-edit-wlan').value.trim() } : {}),
        ...(sheet.querySelector('#dup-edit-lan').value.trim() ? { [MAC_COLUMN_LAN]: sheet.querySelector('#dup-edit-lan').value.trim() } : {}),
        ...(sheet.querySelector('#dup-edit-ip').value.trim() ? { [MAC_COLUMN_IP]: sheet.querySelector('#dup-edit-ip').value.trim() } : {}),
      };
    }

    sheet.querySelector('#dup-edit-queue').addEventListener('click', async () => {
      await enqueue('update_asset', buildEditPayload());
      hapticSuccess();
      showToast('Asset update queued');
      window.dispatchEvent(new CustomEvent('queue-updated'));
      close();
      onClear();
    });

    sheet.querySelector('#dup-edit-sync').addEventListener('click', async () => {
      const syncBtn = sheet.querySelector('#dup-edit-sync');
      syncBtn.disabled = true;
      syncBtn.textContent = 'Syncing…';
      try {
        await syncDirect('update_asset', buildEditPayload());
        hapticSuccess();
        showToast('Asset updated');
        close();
        onClear();
      } catch (err) {
        showToast('Sync failed: ' + err.message);
        syncBtn.disabled = false;
        syncBtn.textContent = 'Sync Now';
      }
    });
  }

  overlay.addEventListener('click', (e) => {
    if (e.target === overlay) { close(); onClear(); }
  });

  renderSummary();
}

// ── Utilities ──────────────────────────────────────────────────────────────

function tryMatchModel(title) {
  const select = document.getElementById('asset-model');
  if (!select) return;
  const t = title.toLowerCase().trim();
  const opts = Array.from(select.options).filter((o) => o.value);
  const match = opts.find((o) => {
    const n = o.textContent.toLowerCase().trim();
    return n === t || t.includes(n) || n.includes(t);
  });
  if (match) match.selected = true;
}

function escapeHtml(str) {
  const div = document.createElement('div');
  div.textContent = str;
  return div.innerHTML;
}

function escapeAttr(str) {
  return (str || '').replace(/"/g, '&quot;').replace(/</g, '&lt;').replace(/>/g, '&gt;');
}
